import React from 'react';
import clsx from 'clsx';
import { has, is } from '../../services/classify';
import { Text, TextMarkup } from '../text';
import { Alignable, TypographyBold, TypographyColor } from '../../objects';
import { checkCents } from './PriceHelpers';
import { hashClass } from '../../helpers';
import { useTrilogyContext } from '../../context';
/**
 * Price Component
 * @param variant {PriceVariant} Variant for Price (PRIMARY|SECONDARY)
 * @param amount {number} Amount for Price
 * @param mention {string} Mention for price ( (1)* )
 * @param period {string} Period for Price (mois)
 * @param showCents {boolean} Display cents
 * @param level {PriceLevel} Price custom size
 * @param huge {boolean} Price huge size
 * @param inverted {boolean} Inverted Price Color
 * @param children {React.ReactNode}
 * @param align {Alignable} Price alignement
 * @param inline {boolean} Inline display Price
 * @param accessibilityLabel {string} Accessibility label
 * @param testId {string} Test Id for Test Integration
 * @param suptitle {string} Price Suptitle
 * @param tagAmount {number} Tag amount
 * @param tagSymbol {number} Tag symbol
 * - -------------------------- WEB PROPERTIES -------------------------------
 * @param className {string} Additionnal CSS Classes
 * @param striked {boolean} Striked Price
 * - --------------- NATIVE PROPERTIES ----------------------------------
 * @param accessibilityActivate {boolean}
 */
const Price = ({ className, variant, amount, mention, period, showCents, level, huge, inverted, align, alert, inline, testId, accessibilityLabel, striked, suptitle, tagAmount, tagSymbol, ...others }) => {
    var _a;
    const { styled } = useTrilogyContext();
    const classes = hashClass(styled, clsx('price', !alert && variant && is(`${variant}`), !variant && alert && is(`${alert}`), level && !huge && is(`level-${level}`), huge && !level && is('huge'), inverted && is('inverted'), inline && is('inlined'), striked && is('striked'), suptitle && has('suptitle'), className));
    const priceParentNode = hashClass(styled, clsx((align == Alignable.ALIGNED_START && has('text-left')) ||
        (align == Alignable.ALIGNED_CENTER && has('text-centered')) ||
        (align == Alignable.ALIGNED_END && has('text-right')) ||
        ''));
    // const fixedCents = (n: any) => {
    //   return n.toString().match(/^-?\d+(?:\.\d{0,2})?/)[0]
    // }
    const isNegative = amount < 0;
    const absoluteAmount = Math.abs(amount);
    // Math.floor on negative decimal decrease its value (as expected), ex: Math.floor(-17.09) => -18
    // Use of Math.abs prevent this in our case
    const absoluteWhole = Math.floor(absoluteAmount);
    const whole = isNegative ? -absoluteWhole : absoluteWhole;
    // Floating point problem Math.floor gives inconsistent results with decimals,
    // Math.round prevents it and is enough with prices decimals
    // ex: (17.09 - 17) * 100 = 8.999999999999986 > Floating point problem
    // ex: Math.round((17.09 - 17) * 100) = 9 > Expected result
    // For more informations https://floating-point-gui.de/
    // const cents = Math.floor((absoluteAmount - absoluteWhole) * 100)
    //   .toString()
    //   .padStart(2, '0')
    let cents = checkCents(((_a = absoluteAmount.toString().split(/[.,]/)[1]) === null || _a === void 0 ? void 0 : _a.substring(0, 2)) || '');
    cents = (cents && cents.length === 1 && `${cents}0`) || cents;
    const centsDisplayed = (inline && showCents && `,${cents || '00'} €`) || (showCents && `€${cents || '00'}`) || '€';
    const returnComponent = (React.createElement("span", { className: tagAmount ? hashClass(styled, clsx(is('aligned-center'), is('flex'))) : '' },
        React.createElement("span", { "data-testid": testId, "aria-label": accessibilityLabel, className: classes, ...others },
            suptitle && React.createElement("span", { className: 'price-suptitle' }, suptitle),
            React.createElement(Text, { className: 'main', markup: TextMarkup.SPAN }, `${whole}`),
            React.createElement("span", { className: hashClass(styled, clsx('price-details')) },
                React.createElement("span", { className: hashClass(styled, clsx('cents')) },
                    inline && centsDisplayed === '€' ? React.createElement(React.Fragment, null,
                        "\u00A0",
                        centsDisplayed) : centsDisplayed,
                    mention && React.createElement("sup", null, mention)),
                period && React.createElement("span", { className: hashClass(styled, clsx('period')) },
                    "/",
                    period))),
        tagAmount && (React.createElement("span", { className: hashClass(styled, clsx(('price-tag'))) },
            React.createElement(Text, { markup: TextMarkup.SPAN, typo: [TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_WHITE] },
                tagAmount,
                " ",
                tagSymbol ? tagSymbol : '€'),
            tagSymbol === '€' && period && React.createElement(Text, { markup: TextMarkup.SPAN, typo: [TypographyBold.TEXT_WEIGHT_NORMAL, TypographyColor.TEXT_WHITE] },
                React.createElement(React.Fragment, null, "\u00A0"),
                "/",
                period)))));
    if (align) {
        return React.createElement("div", { className: priceParentNode }, returnComponent);
    }
    return returnComponent;
};
export default Price;
//# sourceMappingURL=Price.js.map